import { useMemo } from 'react';
import { mapAreaBorder } from '~/_shared/constants/mapObjects/mapAreaBorder.constants';
import { MapAreaColors } from '~/_shared/constants/mapObjects/mapAreaColors.constants';
import { useRequiresCleanMap } from '~/_shared/utils/hooks/useRequiresCleanMap';
import { notNull } from '~/_shared/utils/typeGuards';
import { getBoundaryStyle } from '~/sidebar/sidebarApps/mapTools/boundary/boundaryItem.helpers';
import {
  type BoundaryGroupId, type BoundaryId, type BoundaryIdentifier,
} from '~/store/boundaries/boundaryIdentifier.type';
import { useCustomBoundaryGroupsSelector } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { useMapBoundariesSelector } from '~/store/boundaryItems/boundaryItems.selectors';
import { useActiveBoundaryTerritoryGroupsSelector } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import { useActiveBoundarySelector } from '~/store/frontendState/activeMapElements/activeMapElements.selectors';
import { useHighlightedBoundarySelector } from '~/store/frontendState/mapTools/boundary/boundaryHighlight/boundaryHighlight.selectors';
import { useBoundaryLocationsFilters } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.selectors';
import { type BoundaryItemStyle } from '../../mapBoundary.manager';
import { useMapBoundaryZIndexes } from './useMapBoundaryZIndexes';

export const highlightedBoundaryStyle = {
  borderColor: { value: MapAreaColors.highlight.borderColor },
  borderOpacity: { value: 1 },
  showPolygonOutline: true,
} as const;

export const activeBoundaryBorderAnimationParams = {
  from: MapAreaColors.active.borderColor,
  to: MapAreaColors.active.borderColorAlternative,
};

export const activeBoundaryStyle = {
  borderColor: {
    value: MapAreaColors.active.borderColor,
    animation: activeBoundaryBorderAnimationParams,
  },
  borderOpacity: { value: 1 },
  showPolygonOutline: true,
} as const;

export const isolatedBoundaryStyle = {
  borderColor: { value: MapAreaColors.isolated.borderColor },
  borderOpacity: { value: 1 },
  borderWidth: 2,
} as const;

export type BoundaryExtraStyles = Readonly<{
  boundaryExtraStyles: ReadonlyMap<BoundaryId, {boundary: BoundaryIdentifier; style: BoundaryItemStyle}>;
  boundariesWithExtraStyles: ReadonlySet<BoundaryId>;
}>;

export const useMapBoundaryExtraStyles = (
  baseStyles: ReadonlyMap<BoundaryGroupId, BoundaryItemStyle>,
  boundaryTerritoryStyles: ReadonlyMap<BoundaryGroupId, ReadonlyMap<BoundaryId, BoundaryItemStyle>>,
) => {
  const highlightedBoundary = useHighlightedBoundarySelector();
  const activeBoundary = useActiveBoundarySelector();
  const activeBoundaryTerritoryGroups = useActiveBoundaryTerritoryGroupsSelector();
  const customBoundaryGroups = useCustomBoundaryGroupsSelector();
  const boundaryItems = useMapBoundariesSelector();
  const zIndexes = useMapBoundaryZIndexes();
  const boundaryLocationsFilters = useBoundaryLocationsFilters();
  const cleanMapRequired = useRequiresCleanMap();

  const isolatedBoundaryToBoundaryGroupIdMap = useMemo(() => (
    new Map<BoundaryGroupId, Set<BoundaryId>>(
      boundaryLocationsFilters.isolated.entries.map(item =>
        [item.boundaryGroupId, new Set(item.filter.filteredBoundaries)] as const
      ).filter(notNull)
    )
  ), [boundaryLocationsFilters.isolated.entries]);

  return useMemo<BoundaryExtraStyles>(() => {
    const boundaryExtraStyles = new Map<BoundaryId, {boundary: BoundaryIdentifier; style: BoundaryItemStyle}>();

    for (const boundaryTerritoryGroup of activeBoundaryTerritoryGroups) {
      const customBoundaryGroup = customBoundaryGroups.find(group => group.id === boundaryTerritoryGroup.boundaryGroupId);

      if (!customBoundaryGroup || boundaryTerritoryGroup.settings.boundaryTerritories.length > 0) {
        continue;
      }

      const boundaryBaseStyle = baseStyles.get(boundaryTerritoryGroup.boundaryGroupId);

      if (!boundaryBaseStyle) {
        continue;
      }

      boundaryItems.get(boundaryTerritoryGroup.boundaryGroupId)?.forEach(boundary => {
        const boundaryStyle = getBoundaryStyle(boundaryTerritoryGroup, boundary.id);
        const fillOpacity = boundaryStyle.opacity / 100;

        boundaryExtraStyles.set(boundary.id, { boundary: {
          boundaryId: boundary.id,
          boundaryGroupId: boundaryTerritoryGroup.boundaryGroupId,
        },
        style: {
          ...boundaryBaseStyle,
          fillColor: boundaryStyle.color,
          fillOpacity: { value: fillOpacity },
        } });
      });
    }

    if (!cleanMapRequired) {

      // Active/isolated styles take precedence, don't apply highlighted when already active/isolated
      if (highlightedBoundary?.boundaryId !== activeBoundary?.boundaryId
      && !isolatedBoundaryToBoundaryGroupIdMap.get(highlightedBoundary?.boundaryGroupId || -1)?.has(highlightedBoundary?.boundaryId || -1)
      ) {
        applyActiveStyles({
          boundary: highlightedBoundary, baseStyles, boundaryTerritoryStyles, boundaryExtraStyles,
          partialStyle: { ...highlightedBoundaryStyle, zIndex: zIndexes.highlightedBoundary },
        });
      }

      // Isolated styles take precedence, don't apply active when already isolated
      if (!isolatedBoundaryToBoundaryGroupIdMap.get(activeBoundary?.boundaryGroupId || -1)?.has(activeBoundary?.boundaryId || -1)) {
        applyActiveStyles({
          boundary: activeBoundary, baseStyles, boundaryTerritoryStyles, boundaryExtraStyles,
          partialStyle: { ...activeBoundaryStyle, zIndex: zIndexes.activeBoundary },
        });
      }

      isolatedBoundaryToBoundaryGroupIdMap.forEach((boundaryIds, boundaryGroupId) => {
        boundaryIds.forEach(boundaryId => {
          applyActiveStyles({
            boundary: { boundaryId, boundaryGroupId }, baseStyles, boundaryTerritoryStyles, boundaryExtraStyles,
            partialStyle: { ...isolatedBoundaryStyle, zIndex: zIndexes.activeBoundary },
          });
        });
      });
    }

    return { boundaryExtraStyles, boundariesWithExtraStyles: new Set(boundaryExtraStyles.keys()) };
  }, [cleanMapRequired, activeBoundaryTerritoryGroups, customBoundaryGroups, baseStyles, boundaryItems, highlightedBoundary,
    activeBoundary, isolatedBoundaryToBoundaryGroupIdMap, boundaryTerritoryStyles, zIndexes.highlightedBoundary, zIndexes.activeBoundary]);
};

type PartialBoundaryItemStyles = Omit<Partial<BoundaryItemStyle>, 'zIndex'> & { zIndex: Partial<BoundaryItemStyle['zIndex']> };

const getActiveStyles = (
  baseStyle: BoundaryItemStyle,
  partialStyle: PartialBoundaryItemStyles,
): BoundaryItemStyle => {
  return ({
    ...baseStyle,
    ...partialStyle,
    zIndex: { ...baseStyle.zIndex, ...partialStyle.zIndex },
    borderWidth: partialStyle.borderWidth ?? Math.max(baseStyle.borderWidth + 2, mapAreaBorder.active.minWidth),
  });
};

const applyActiveStyles = ({ boundary, partialStyle, baseStyles, boundaryTerritoryStyles, boundaryExtraStyles }: {
  boundary: { boundaryId: BoundaryId; boundaryGroupId: BoundaryGroupId} | null;
  partialStyle: PartialBoundaryItemStyles;
  baseStyles: ReadonlyMap<BoundaryGroupId, BoundaryItemStyle>;
  boundaryTerritoryStyles: ReadonlyMap<BoundaryGroupId, ReadonlyMap<BoundaryId, BoundaryItemStyle>>;
  boundaryExtraStyles: Map<BoundaryId, { boundary: BoundaryIdentifier; style: BoundaryItemStyle }>;
}) => {
  if (!boundary) {
    return;
  }

  const currentBoundaryStyle = boundaryExtraStyles.get(boundary.boundaryId)?.style
    ?? boundaryTerritoryStyles?.get(boundary.boundaryGroupId)?.get(boundary.boundaryId)
    ?? baseStyles.get(boundary.boundaryGroupId);

  if (boundary && currentBoundaryStyle) {
    const style = getActiveStyles(currentBoundaryStyle, partialStyle);
    boundaryExtraStyles.set(boundary.boundaryId, { boundary, style });
  }
};
